import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { userService } from "../../services/api";
import { ROLES } from "../../config/roles";

export const useHeaderProfile = () => {
  const [profileData, setProfileData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { currentRole } = useSelector((state) => state.auth);

  useEffect(() => {
    const fetchProfile = async () => {
      setIsLoading(true);
      try {
        let response;
        // Fetch appropriate profile based on current role
        if (currentRole === ROLES.Staff) {
          response = await userService.getStaffProfile();
        } else if (currentRole === ROLES.Coach) {
          response = await userService.getCoachProfile();
        } else {
          response = await userService.getProfile();
        }

        if (response.success) {
          console.log("Profile data:", response.data);
          setProfileData(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch profile:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, [currentRole]);

  const getProfileDisplay = (profileData) => {
    // Only show club profile for ClubAdmin role
    if (profileData?.club_details?.name && currentRole === ROLES.ClubAdmin) {
      // This is a club admin profile
      return {
        name: profileData.club_details.name,
        initials: getInitials(profileData.club_details.name),
        email: profileData.club_details.contact_person_email,
        image: profileData.club_details.logo,
        transferPoints: profileData.club_details.transfer_points,
        contactPerson: profileData.club_details.contact_person_name,
        position: profileData.club_details.contact_person_position,
      };
    } else {
      // This is a staff/coach/player/agent profile
      const name = profileData.name || profileData.user_details?.name;
      const role = profileData.role || profileData.user_details?.position;
      
      return {
        name: name,
        initials: getInitials(name),
        email: profileData.email,
        transferPoints: profileData.transfer_points,
        staffType: profileData.user_details?.staff_type,
        roleType: profileData.user_details?.role_type,
        licenseNumber: profileData.user_details?.license_number,
        role: role,
        // For coaches/staff with club, show club contact info if available
        contactPerson: profileData.club_details?.contact_person_name,
        position: profileData.club_details?.contact_person_position || role,
      };
    }
  };

  const getInitials = (name) => {
    if (!name) return "";
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getNameByRole = (role = currentRole, data = profileData) => {
    if (!data) return "";

    switch (role) {
      case ROLES.ClubAdmin:
        return data.club_details?.name || data.name || "";
      case ROLES.AgencyAdmin:
        return data.agency_details?.name || data.name || "";
      case ROLES.Agent:
        return data.name || "";
      case ROLES.Staff:
        return data.name || "";
      case ROLES.Coach:
        return data.name || data.user_details?.name || "";
      case ROLES.Player:
        return data.name || "";
      case ROLES.Organizer:
        return data.name || "";
      default:
        return data.name || "";
    }
  };

  const getInitialsForRole = (role = currentRole, data = profileData) => {
    const roleName = getNameByRole(role, data);
    return roleName ? getInitials(roleName) : "";
  };

  const getProfilePicAccordingToRole = (
    role = currentRole,
    data = profileData
  ) => {
    if (!data) return null;

    switch (role) {
      case ROLES.ClubAdmin:
        return data.club_details?.logo || null;
      case ROLES.AgencyAdmin: {
        if (data.agency_details?.profile_picture_url) {
          return data.agency_details.profile_picture_url;
        }
        return data.profile_picture_url || null;
      }
      case ROLES.Agent:
        return data.profile_picture_url || null;
      case ROLES.Staff:
        return data.profile_picture_url || null;
      case ROLES.Coach:
        // Coach profile picture can be in user_details or top level
        return data.user_details?.profile_picture_url || data.profile_picture_url || null;
      case ROLES.Player:
        return data.profile_picture_url || null;
      case ROLES.Organizer:
        return data.profile_picture_url || null;
      default:
        return data.profile_picture_url || null;
    }
  };

  return {
    profileData,
    isLoading,
    getProfileDisplay,
    getInitials,
    getNameByRole,
    getInitialsForRole,
    getProfilePicAccordingToRole,
  };
};
